import React, {
  useState, useEffect, useMemo, useRef,
} from 'react'
import { useForm } from 'react-hook-form'
import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useDispatch, useSelector } from 'react-redux'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { selectFulfillmentCenters } from 'src/features/selector'
import { actions } from 'src/features/state'
import { actions as appActions } from '../../features/state'
import { getStockMovimentations } from 'src/services/stock/stockService'
import TableMui from 'src/components/TableMui'
import * as S from './Movimentation.styles'
import { toast } from 'react-toastify'

interface MovimentationRecord {
  collaborator: string
  type: string
  productCode: string | number
  description: string
  quantity: number
  measureUnitType: string
  address: string
  justification: string
  movimentationDate: string
  expirationDate: string
  balance: number
  fulfillmentCenter: {
    id: number
    name: string
  }
}

interface MovimentationRow extends Omit<MovimentationRecord, 'fulfillmentCenter'> {
  fulfillmentCenterName: string
}

interface FormValues {
  dateFrom: string
  dateTo: string
  fulfillmentCenterId: number | string
  product: string
  address: string
}

interface SearchParams {
  dateFrom: string
  dateTo: string
  fulfillmentCenterId: number | string
  product: string
  address: string
}

interface Pagination {
  currentPage: number
  perPage: number
  totalItems: number
  totalPages: number
}

const orderByMap: Record<string, string> = {
  fulfillmentCenterName: 'fc_name',
  collaborator: 'user',
  type: 'movement_type',
  productCode: 'product_code',
  description: 'product_description',
  quantity: 'quantity',
  measureUnitType: 'measure_unit_type',
  balance: 'balance',
  address: 'address',
  justification: 'movement_justification',
  movimentationDate: 'movement_date',
  expirationDate: 'expiration_date',
}

const isDateRangeGreaterThan15Days = (dateFrom: string, dateTo: string): boolean =>
  Math.abs(dayjs(dateTo).diff(dateFrom, 'day')) > 15

const toMovimentationRow = (record: MovimentationRecord): MovimentationRow => ({
  ...record,
  fulfillmentCenterName: record.fulfillmentCenter.name,
})

const Movimentation = () => {
  const dispatch = useDispatch()
  const [movimentations, setMovimentations] = useState<MovimentationRecord[]>([])
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    perPage: 25,
    totalItems: 0,
    totalPages: 0,
  })
  const lastSearchParams = useRef<SearchParams | null>(null)
  const fulfillmentCenters = useSelector(selectFulfillmentCenters)

  const {
    register, handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      dateFrom: '',
      dateTo: '',
      fulfillmentCenterId: '',
      product: '',
      address: '',
    },
  })

  const fulfillmentCenterOptions = useMemo(
    () => fulfillmentCenters.map(({ id, name }) => ({ id, description: name })),
    [fulfillmentCenters],
  )

  const movimentationRows = useMemo<MovimentationRow[]>(
    () => movimentations.map(toMovimentationRow),
    [movimentations],
  )

  const fetchMovimentations = async (
    searchParams: SearchParams,
    page: number,
    perPage: number,
    order?: string,
    orderBy?: string | number,
  ): Promise<void> => {
    try {
      dispatch(appActions.isLoading(true))

      const response = await getStockMovimentations({
        ...searchParams,
        page,
        perPage,
        order,
        orderBy,
      })

      if (!response.isOk) {
        toast.error(response?.data?.message || 'Falha ao listar as movimentações!')
        return
      }

      setMovimentations(response.data.movimentations)
      setPagination(response.data.pagination)
    } catch {
      toast.error('Falha ao listar as movimentações')
    } finally {
      dispatch(appActions.isLoading(false))
    }
  }

  const onSubmit = async ({
    dateFrom, dateTo, fulfillmentCenterId: fcId, product, address,
  }: FormValues): Promise<void> => {
    setMovimentations([])
    setErrorMessage(undefined)

    if (isDateRangeGreaterThan15Days(dateFrom, dateTo)) {
      setErrorMessage('Selecione datas com no máximo 15 dias de diferença')
      return
    }

    const searchParams: SearchParams = {
      dateFrom, dateTo, fulfillmentCenterId: fcId, product, address,
    }
    lastSearchParams.current = searchParams
    await fetchMovimentations(searchParams, 1, pagination.perPage)
  }

  const getPaginatedItems = (page: number, perPage: number, order?: string, orderBy?: string | number) => {
    if (!lastSearchParams.current) return
    const mappedOrderBy = orderBy ? (orderByMap[orderBy] ?? orderBy) : orderBy
    fetchMovimentations(lastSearchParams.current, page, perPage, order, mappedOrderBy)
  }

  useEffect(() => {
    dispatch(actions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  return (
    <>
      <S.Container>
        <Typography variant='h5' fontWeight='bold'>Movimentações Operacionais</Typography>

        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1rem',
            width: '100%',
          }}
        >
          <S.CustomInputText
            name='dateFrom'
            label='Data Inicial'
            type='date'
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...register('dateFrom', { required: true }) }}
          />
          <S.CustomInputText
            name='dateTo'
            label='Data Final'
            type='date'
            InputLabelProps={{ shrink: true }}
            inputProps={{ ...register('dateTo', { required: true }) }}
          />
          <S.CustomSelect
            name='fulfillmentCenterId'
            label='FC'
            inputProps={{ ...register('fulfillmentCenterId') }}
            options={fulfillmentCenterOptions}
            dataTestId='fc-select'
            fullWidth={false}
          />
          <S.CustomInputText
            name='product'
            label='Produto'
            type='text'
            inputProps={{ ...register('product') }}
          />
          <S.CustomInputText
            name='address'
            label='Endereço'
            type='text'
            inputProps={{ ...register('address') }}
          />
          <S.CustomButton
            title='Buscar'
            type='submit'
            bgColor='blue'
            hoverColor='darkBlueHover'
            letterColor='white'
          />
        </Box>

        {errorMessage && <S.ErrorSummary>{errorMessage}</S.ErrorSummary>}

        <TableMui
          columns={[
            { id: 'fulfillmentCenterName', label: 'FC' },
            { id: 'collaborator', label: 'Operador' },
            { id: 'type', label: 'Tipo' },
            { id: 'productCode', label: 'Cod. produto' },
            { id: 'description', label: 'Produto' },
            { id: 'quantity', label: 'Qtd.' },
            { id: 'balance', label: 'Novo Saldo' },
            { id: 'measureUnitType', label: 'Un. medida' },
            { id: 'address', label: 'Localização' },
            { id: 'justification', label: 'Justificativa' },
            { id: 'movimentationDate', label: 'Data' },
            {
              id: 'expirationDate', label: 'Validade', isDate: true, dateFormat: 'DD/MM/YYYY',
            },
          ]}
          emptyLabel='Utilize o filtro para buscar movimentações'
          rows={movimentationRows}
          rowCount={movimentationRows.length}
          minHeight='64vh'
          maxHeight='64vh'
          enablePagination
          rowsPerPageOptions={[10, 25, 30, 50, 100]}
          rowsPerPageLimit={pagination.perPage}
          currentPage={pagination.currentPage - 1}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          getPaginatedItems={getPaginatedItems}
          resetPageOnRowsChange={false}
          hasSorting
          thinLine
        />
      </S.Container>
    </>
  )
}

export default Movimentation
